import {getCookie} from 'common/util/cookie';

class MpFetchError extends Error {
  constructor(error: Error) {
    super(error.message);
    this.stack = error.stack;
    Object.assign(this, error);
    // Assigned after Object.assign so this literal can't be clobbered by an own
    // `name` property on the wrapped error. Preserve `AbortError` so the many
    // call sites that detect aborted fetches via `err.name === 'AbortError'`
    // keep working; otherwise stamp a literal `MpFetchError` so Sentry's
    // name-based ignore filter still matches under bundler minification.
    this.name = error.name === `AbortError` ? `AbortError` : `MpFetchError`;
  }
}

const csrfSafeMethod = (method?: string) => (method ? /^(GET|HEAD|OPTIONS|TRACE)$/.test(method) : true);
const isSameOrigin = (request: Request | string | URL) => {
  let url: string = ``;
  if (request instanceof URL) {
    url = request.toString();
  } else if (request instanceof Request) {
    url = request.url;
  } else {
    url = request;
  }

  return url.startsWith(`/`) || (url.startsWith(`https://`) && window.location.origin === new URL(url).origin);
};

// don't double patch fetch
if (!window[`_fetch`]) {
  const originalFetch = window.fetch;
  // patch window.fetch to always send X-CSRF header on same-origin requests that can modify resources
  window[`_fetch`] = originalFetch;
  window.fetch = async function (url, options = {}) {
    const method = options && options.method;
    // same-origin check so we're not sending csrf tokens to non mixpanel.com domains
    if (!csrfSafeMethod(method) && isSameOrigin(url)) {
      // Read per-request, not at module load: Django rotates csrftoken on
      // (re)login, so a captured token goes stale and strict-CSRF endpoints 403.
      const incomingHeaders =
        options.headers instanceof Headers ? Object.fromEntries(options.headers.entries()) : options.headers;
      const headers = {...incomingHeaders, 'X-CSRFToken': getCookie(`csrftoken`)};
      options = {...options, headers, credentials: `same-origin`};
    }

    try {
      const response = await originalFetch(url, options);
      return response;
    } catch (error) {
      throw new MpFetchError(error);
    }
  };
}

// If the csrftoken cookie is missing, ask the server to re-seed it.
// This is likely to happen in regional subdomains (eg. eu.mixpanel.com)
// because the CSRF cookie is typically set at login on mixpanel.com and doesn't allow subdomains.
if (!getCookie(`csrftoken`)) {
  fetch(`/renew-csrf`, {method: `GET`, credentials: `same-origin`})
    .then((response) => {
      if (!response.ok) {
        console.error(`Failed to renew csrftoken cookie: HTTP ${response.status}`);
      }
    })
    .catch((error) => {
      console.error(`Failed to renew csrftoken cookie:`, error);
    });
}
